import { Button as MuiButton, Stack, Typography as MuiTypography } from "@mui/material";
import { styled } from "@mui/styles";
import { ReplyDoubleArrowIcon, ReplyIcon } from "components/icons";
import { KeyConstant, LangConstant, SystemConstant } from "const";
import { memo, useEffect, useState } from "react";
import { getInteractor } from "services/local.service";
import { StorageUtil } from "utils";
import { getCommonLang, getNSLang } from "utils/lang.utils";
import { getLatestMessage } from "utils/view.utils";
import { getPinContent, replaceId2Name } from "utils/message.utils";
import { useDispatch, useSelector } from "react-redux";
import { ConversationActions, ConversationSelectors } from "redux-store";
import { createSelector } from "reselect";
import { useCleanUpEffect } from "hooks";
import { useConversationContext } from "../ConversationContext";
import DecryptMessageView from "./DecryptMessageView";

const updateMsgReduxState = createSelector(
  [
    ConversationSelectors.getSelectedGroupId,
    ConversationSelectors.getThreadingId,
    state => state.conversationRedux.incomingMessages,
  ],
  (selectedGroupId, selectedThreadId, incomingMessages) => {
    const currentIncomingMessage = Object.values(incomingMessages[selectedThreadId || selectedGroupId] || {}) || [];
    const updateMessageSourceIds = currentIncomingMessage
      .filter(
        item =>
          [SystemConstant.SEND_TYPE.deleteMessage, SystemConstant.SEND_TYPE.deleteMessageOnlyMe].includes(
            item.sendType,
          ) ||
          (SystemConstant.EDITABLE_SEND_TYPE.includes(item.sendType) && Boolean(item.parentId)),
      )
      .map(item => item.parentId);
    return { updateMessageSourceIds };
  },
);

const ThreadParentLine = ({ message }) => {
  const dispatch = useDispatch();
  const prefixKey = StorageUtil.getCurrentPrefixKey();
  const accountId = StorageUtil.getItem(KeyConstant.KEY_ACCOUNT_ID, prefixKey);
  const selectedThreadId = useSelector(ConversationSelectors.getThreadingId);
  const { updateMessageSourceIds } = useSelector(updateMsgReduxState);
  const isThreadMode = Boolean(selectedThreadId);
  const { isMounted } = useCleanUpEffect();
  const { groupDetail } = useConversationContext();

  const isMine = message.senderId === accountId;

  const [parentContent, setParentContent] = useState("");
  const [parentMessage, setParentMessage] = useState({});
  const [isDecryptFailed, setIsDecryptFailed] = useState(false);

  const handleGetParentMsg = async message => {
    const parentMessageDetail = await getInteractor(prefixKey).LocalMessageService.findOne({
      source_id: message.threadId,
      state: SystemConstant.STATE.active,
    });
    setParentMessage(parentMessageDetail);
    if (parentMessageDetail && parentMessageDetail.id) {
      handleSetParentContent(parentMessageDetail);
    } else if (isMounted()) {
      setParentContent(getCommonLang("TXT_NOT_FOUND_MESSAGE"));
    }
  };

  const handleSetParentContent = async parentMessage => {
    const latestMessage = await getLatestMessage(prefixKey, parentMessage);
    latestMessage.content = await replaceId2Name(latestMessage.content, groupDetail.groupMembers, false);

    const countDelete = await getInteractor(prefixKey).LocalMessageService.count({
      parent_id: message.threadId,
      send_type: SystemConstant.SEND_TYPE.deleteMessage,
    });
    const countDeleteOnlyMe = await getInteractor(prefixKey).LocalMessageService.count({
      parent_id: message.threadId,
      send_type: SystemConstant.SEND_TYPE.deleteMessageOnlyMe,
    });

    if (isMounted()) {
      if (latestMessage.sendType === SystemConstant.SEND_TYPE.notFound) {
        setParentContent(getCommonLang("TXT_NOT_FOUND_MESSAGE"));
      } else if (countDeleteOnlyMe > 0) {
        setParentContent(getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_ONLY_ME"));
      } else if (countDelete > 0) {
        setParentContent(getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_DELETE_MSG_ALL"));
      } else {
        const content = getPinContent(latestMessage);
        if (content && content.length > MAX_PARENT_CONTENT_LENGTH) {
          const newContent = content.slice(0, MAX_PARENT_CONTENT_LENGTH);
          setParentContent(`${newContent}...`);
        } else {
          setParentContent(content);
        }
      }
    }
  };

  const handleJump2Thread = event => {
    event.stopPropagation();

    dispatch(
      ConversationActions.setSelectGroupId({
        selectedGroupId: message.groupId,
        threadingId: message.threadId,
        scrollToChildId: message.state === SystemConstant.STATE.active ? message.sourceId : null,
      }),
    );
  };

  useEffect(() => {
    if (message && message.id) handleGetParentMsg(message);
  }, [message]);

  useEffect(() => {
    if (updateMessageSourceIds.includes(message.threadId)) {
      handleGetParentMsg(message);
    }
  }, [updateMessageSourceIds]);

  const isShowDecryptMessageView =
    parentMessage?.message_encrypt_status &&
    [
      SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptRecover,
      SystemConstant.MESSAGE_ENCRYPT_STATUS.decryptFailed,
    ].includes(parentMessage.message_encrypt_status);

  return (
    <ThreadLineComponent justifyContent={isMine ? "end" : "start"}>
      {isThreadMode ? (
        <>
          <ReplyDoubleArrowIcon width={15} height={15} />
          <Typography>{getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_REPLY_TO_CHANNEL")}</Typography>
        </>
      ) : (
        <>
          <ReplyIcon width={15} height={15} />
          <Typography>{getNSLang(LangConstant.NS_HOME_CONVERSATION, "TXT_REPLY_IN_THREAD")}</Typography>
          <Button onClick={handleJump2Thread}>
            {isShowDecryptMessageView ? (
              <DecryptMessageView
                created={parentMessage.created}
                isMine={true}
                isDecryptFailed={isDecryptFailed}
                setIsDecryptFailed={setIsDecryptFailed}
              />
            ) : (
              parentContent
            )}
          </Button>
        </>
      )}
    </ThreadLineComponent>
  );
};

export default memo(ThreadParentLine);

const MAX_PARENT_CONTENT_LENGTH = 23;

const ThreadLineComponent = styled(Stack)({
  width: "100%",
  height: 20,
  flexDirection: "row",
  alignItems: "center",
  marginBottom: 4,
  gap: 4,
});

const Button = styled(MuiButton)({
  minWidth: "unset",
  textTransform: "none",
  padding: 0,
  fontSize: 12,
});

const Typography = styled(MuiTypography)(props => ({
  fontSize: 12,
  color: props.theme.palette.grey[300],
}));
